<template>
    <div id="fleet-box">
        <div id="fleet-search">
            <div class="fleet-search-header">
<!--                <h2>{{ $t('dashboard.fleetList.header') }}</h2>-->
                <button></button>
                <sp-button class="fleet-search-header__get-fleets" @click.native="getFleets" :icon-width="25" :icon-height="25" icon='reset'></sp-button>
                <sp-button @click.native="isDescending = !isDescending" :icon-width="30" :icon-height="30" :icon="isDescending ? 'z-to-a' : 'a-to-z'"></sp-button>
                <sp-button v-if="isActive === false" class="fleets-btn" @click.native="toggle" text="Extend all"></sp-button>
                <sp-button v-if="isActive === true" class="fleets-btn--hide" @click.native="toggle" text="Hide All"></sp-button>
            </div>
            <div class="input-wrapper">
                <sp-input ds-id="search-vehicle" v-model="search" ds-name="search" ds-type="text" :ds-placeholder="$t('dashboard.fleetList.searchInput')" ds-class="dark icon icon-search"></sp-input>
            </div>
        </div>
        <div id="fleet-list">
            <fleet v-for="fleetChild in sortedFleets" v-bind:key="fleetChild.id"
                   :fleet="fleetChild"
                   :update-fleets-overview="updateFleetsOverview"
                   :expanded-by-parent="isActive"
            >
            </fleet>
        </div>
    </div>
</template>

<script>
import SpInput from '@/components/forms/Input'
import Fleet from '@/views/Dashboard/components/Fleet'
import SpButton from '@/components/forms/Button'
import { mapActions, mapGetters } from 'vuex'

import VehicleApi from '@/api/VehicleApi'
import StatisticsApi from '@/api/StatisticsApi'

import CollectionTransformer from '@/transformers/CollectionTransformer'
import FleetGroupedTransformer from '@/transformers/FleetGroupedTransformer'
import { FLEET_STATISTICS_TIME_INTERVAL, VEHICLE_INACTIVE, VEHICLE_OFFLINE, VEHICLE_ONLINE } from '@/config/tackingConfig'
import PerfectScrollbar from 'perfect-scrollbar'
import _ from 'lodash'

const vehicleApi = new VehicleApi()
const statisticsApi = new StatisticsApi()
const collectionTransformer = new CollectionTransformer()
const fleetTransformer = new FleetGroupedTransformer()

export default {
    name: 'FleetList',
    components: {
        SpButton,
        SpInput,
        Fleet
    },
    async mounted () {
        let ps = new PerfectScrollbar('#fleet-box', {
            wheelSpeed: 2,
            wheelPropagation: true,
            minScrollbarLength: 20
        })
        ps.update()

        await this.getFleets()
        this.fleetsFilteredByName = _.cloneDeep(this.fleets)
        this.fleetsToSort = _.cloneDeep(this.fleets)
        this.clearTrackingVehicles()
    },
    beforeDestroy () {
        clearInterval(this.statisticsInterval)
    },
    data () {
        return {
            isWaiting: false,
            isDescending: false,
            fleets: [],
            fleetsFilteredByName: [],
            fleetsToSort: [],
            search: null,
            loading: true,
            statisticsInterval: null,
            value: null,
            isActive: false
        }
    },
    computed: {
        sortedFleets: function () {
            return _.orderBy(this.fleetsToSort, [fleet => fleet.name.toLowerCase()], this.isDescending ? 'desc' : 'asc')
        },
        ...mapGetters({
            getFilters: 'trackingModule/getFilters',
            getDeviceStatistics: 'trackingModule/getDeviceStatistics',
            getSubscribedVehiclesFleetCounter: 'trackingModule/getSubscribedVehiclesFleetCounter',
            asideBarHasChanged: 'organizationTreeModule/asideBarHasChanged'
        })
    },
    watch: {
        search (value) {
            this.filterByName(value)
        },
        getFilters: function (newVal, oldVal) {
            this.checkAvailableCars()
        },
        asideBarHasChanged: {
            handler: async function (value) {
                if (value) {
                    this.$store.commit('organizationTreeModule/setAsideBarHasChanged', false)

                    await this.getFleets()
                    this.fleetsFilteredByName = _.cloneDeep(this.fleets)
                    this.fleetsToSort = _.cloneDeep(this.fleets)
                    this.clearTrackingVehicles()
                }
            },
            deep: true
        }
    },
    methods: {
        ...mapActions({
            setDeviceSummary: 'trackingModule/setDeviceSummary',
            setDeviceStatistics: 'trackingModule/setDeviceStatistics',
            trackingSubscribeVehicle: 'trackingModule/subscribeTrackingVehicle',
            trackingUnsubscribeVehicle: 'trackingModule/unsubscribeTrackingVehicle',
            clearTrackingVehicles: 'trackingModule/clearTrackingVehicles'
        }),
        toggle () {
            this.isActive = !this.isActive;
            const allVehicles = [];

            this.sortedFleets.forEach(fleet => allVehicles.push(...fleet.vehicles));

            if (this.isActive) {
                allVehicles.forEach(vehicle => {
                    this.trackingSubscribeVehicle(vehicle)
                })
            } else {
                allVehicles.forEach(vehicle => {
                    this.trackingUnsubscribeVehicle(vehicle)
                })
            }
            this.updateFleetsOverview()
        },
        async getFleets () {
            clearInterval(this.statisticsInterval)

            this.setDeviceSummary({
                devicesInactive: 0,
                devicesOnline: 0,
                devicesOffline: 0
            })
            this.fleets = []

            const vehicleResponse = await vehicleApi.getGroupedVehicles()
            const { structure } = vehicleResponse.payload

            const active = structure[0]

            if (null == active) {
                return
            }

            this.fleets = collectionTransformer.transform(fleetTransformer, active.fleets)

            await this.getFleetStatistics()

            this.statisticsInterval = setInterval(() => {
                this.getFleetStatistics()
            }, FLEET_STATISTICS_TIME_INTERVAL)
        },
        async getFleetStatistics () {
            const statisticsResponse = await statisticsApi.getDeviceStatistics()
            this.setDeviceStatistics(statisticsResponse.payload)
            const { devicesInactive, devicesOffline, devicesOnline, devicesSummary, fleetsCounters } = statisticsResponse.payload

            // updating vehicle statuses
            this.updateVehicleStatuses(devicesInactive, devicesOffline, devicesOnline)

            const fleets = []
            for (let key in this.getSubscribedVehiclesFleetCounter) {
                if (0 === this.getSubscribedVehiclesFleetCounter[key]) {
                    continue
                }

                fleets.push(key)
            }

            // update global statistics
            if (_.isEmpty(fleets)) {
                this.setDeviceSummary(devicesSummary)
            } else {
                let summary = {
                    devicesInactive: 0,
                    devicesOnline: 0,
                    devicesOffline: 0
                }
                fleets.map((key) => {
                    summary.devicesInactive += fleetsCounters[key] ? fleetsCounters[key].devicesInactive : 0
                    summary.devicesOnline += fleetsCounters[key] ? fleetsCounters[key].devicesOnline : 0
                    summary.devicesOffline += fleetsCounters[key] ? fleetsCounters[key].devicesOffline : 0
                })

                this.setDeviceSummary(summary)
            }
        },
        updateFleetsOverview () {
            const { devicesSummary, fleetsCounters } = this.getDeviceStatistics

            const fleets = []

            for (let key in this.getSubscribedVehiclesFleetCounter) {
                if (0 === this.getSubscribedVehiclesFleetCounter[key]) {
                    continue
                }

                fleets.push(key)
            }

            // update global statistics
            if (_.isEmpty(fleets)) {
                this.setDeviceSummary(devicesSummary)
            } else {
                let summary = {
                    devicesInactive: 0,
                    devicesOnline: 0,
                    devicesOffline: 0
                }
                fleets.map((key) => {
                    summary.devicesInactive += fleetsCounters[key].devicesInactive
                    summary.devicesOnline += fleetsCounters[key].devicesOnline
                    summary.devicesOffline += fleetsCounters[key].devicesOffline
                })

                this.setDeviceSummary(summary)
            }
        },
        updateVehicleStatuses (devicesInactive, devicesOffline, devicesOnline) {
            for (const fleet of this.fleets) {
                for (let vehicle of fleet.vehicles) {
                    if (devicesOffline.includes(vehicle.trackingHardwareId)) {
                        vehicle.status = VEHICLE_OFFLINE
                        continue
                    }

                    if (devicesOnline.includes(vehicle.trackingHardwareId)) {
                        vehicle.status = VEHICLE_ONLINE
                        continue
                    }

                    if (devicesInactive.includes(vehicle.trackingHardwareId)) {
                        vehicle.status = VEHICLE_INACTIVE
                    }
                }
            }
        },
        checkAvailableCars () {
            this.fleetsToSort = _.cloneDeep(this.fleetsFilteredByName.filter(el => true === el.isVisible))
            this.fleetsToSort.forEach(fleet => {
                let hasVisibleVehicles = false
                fleet.vehicles.forEach(vehicle => {
                    vehicle.isVisible = this.getFilters.includes(vehicle.status) && vehicle.isVisible
                    hasVisibleVehicles = hasVisibleVehicles || vehicle.isVisible
                })
                fleet.isVisible = hasVisibleVehicles
            })
        },
        filterByName (value) {
            const searchRegex = (value || '').toLowerCase()
            let fleets = _.cloneDeep(this.fleets)
            fleets.map((fleet) => {
                let hasVehicleVisible = false
                fleet.vehicles.forEach((vehicle) => {
                    if (vehicle.registrationNumber.toLowerCase().includes(searchRegex) || (vehicle.trackingHardwareId || '').toLowerCase().includes(searchRegex)) {
                        hasVehicleVisible = true
                        vehicle.isVisible = true
                    } else {
                        vehicle.isVisible = false
                    }
                })

                fleet.isVisible = fleet.name.toLowerCase().includes(searchRegex) || hasVehicleVisible

                if (fleet.name.toLowerCase().includes(searchRegex)) {
                    fleet.vehicles.forEach((vehicle) => {
                        vehicle.isVisible = true
                    })
                }
            })
            this.fleetsFilteredByName = _.cloneDeep(fleets.filter(el => true === el.isVisible))
            this.checkAvailableCars()
        }
    }
}
</script>

<style scoped lang="scss">
#fleet-search {
    border-left: get($left-border, width) solid $primary;
    padding: 0 40px 35px;

    h2 {
        margin: 0;
        font-weight: 500;
        font-size: 1.9rem;
    }

    .fleet-search-header {
        display: flex;
        align-items: center;
        padding: 25px 0 13px;
        &__get-fleets {
            margin-right: 10px;
        }
        .icon {
            display: inline-block;
        }
        h2 {
            color: $font;
        }
    }

    .fleets-btn {
        margin-left: 10px;

        @media (min-width: 1440px) {
            margin-left: 54px;
        }

        ::v-deep .btn .text {
            background-color: var(--dynamic-primary);
            color: var(--dynamic-icon-primary);
            padding: 0 13px;

            @media (min-width: 1440px) {
                padding: 0 20px;
            }
        }

        &--hide {
            margin-left: 10px;

            @media (min-width: 1440px) {
                margin-left: 74px;
            }

            ::v-deep .btn .text {
                background-color: var(--dynamic-primary);
                padding: 0 13px;

                @media (min-width: 1440px) {
                    padding: 0 20px;
                }
            }
        }
    }

    .form-group {
        margin-bottom: 0;
    }
}

#fleet-box {
    overflow: hidden;
    position: relative;
    height: calc(100vh - (259px + 40px));
}
</style>
